import { useState } from "react";
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { useSwipeable } from "react-swipeable";
import styled from "@emotion/styled";

import { Flex } from "./shared";
import { mq } from "./responsive";

const active = {
  width: "8px",
  height: "8px",
  backgroundColor: "#E5F8E5",
};

const IndicatorItem = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #d8d8d8;
  margin: 1rem 0;
`;

const PhotoIndicator = styled.div(
  {
    height: "9rem",
    width: "14rem",
    minWidth: "14rem",
    borderRadius: "0.5rem",
    position: "relative",
    marginLeft: "3rem",

    "&:first-of-type": {
      marginLeft: 0,
    },
  },
  (props) => ({
    backgroundImage: `url("${props.img}")`,
    backgroundSize: "cover",

    "&:before": {
      content: `""`,
      display: !props.active ? "block" : "none",
      backgroundColor: "#ffffff",
      opacity: 0.4,
      position: "absolute",
      width: "100%",
      height: "100%",
    },
  })
);

const Container = styled.div({
  borderRadius: "1rem",
  overflow: "hidden",
  position: "relative",
  flexGrow: "2",
  height: "100%",
  [mq.md]: {
    display: "flex",
    overflowX: "auto",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
});

const CarouselItem = styled.div(
  {
    position: "absolute",
    width: "100%",
    height: "100%",
    borderRadius: "1rem",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: "all 0.5s ease-out",

    "& > *": {
      width: "100%",
    },
  },
  (props) => ({
    transform: props.isCurrent ? "scale(1)" : "scale(0.9)",
  })
);

const Carousel = ({
  width,
  height = "22rem",
  render,
  items,
  indicatorType = "photos",
  direction = "horizontal",
  collapse,
  indicatorStyle = {},
}) => {
  const [current, setCurrent] = useState(0);
  const itemPosition = (i) => {
    const property = direction === "vertical" ? "top" : "left";
    return {
      [property]:
        i === current
          ? "0"
          : i > current
          ? `${100 + (i - current) * 100}%`
          : `${(i - current) * 100}%`,
    };
  };

  const nextItem = () =>
    setCurrent((current) =>
      current + 1 >= items.length ? current : current + 1
    );

  const prevItem = () =>
    setCurrent((current) => (current - 1 < 0 ? current : current - 1));

  let events;

  if (direction === "vertical")
    events = {
      onSwipedUp: (e) => nextItem(),
      onSwipedDown: (e) => prevItem(),
    };
  else
    events = {
      onSwipedRight: (e) => prevItem(),
      onSwipedLeft: (e) => nextItem(),
    };

  const handlers = useSwipeable({
    ...events,
    preventDefaultTouchmoveEvent: true,
  });

  return (
    <Flex
      css={{
        flexDirection: "column",
        position: "relative",
        ...(indicatorType === "dots"
          ? { filter: "drop-shadow(0px 6px 14px rgba(0, 0, 0, 0.06))" }
          : {}),
      }}
    >
      <Flex
        css={{
          alignItems: "center",
          justifyContent: "space-between",
          width: width || "auto",
          flexWrap: "wrap",
          height: collapse ? "fit-content" : height,
        }}
        {...handlers}
      >
        <Container>
          {items.map((item, i) => (
            <CarouselItem
              key={i}
              isCurrent={current === i}
              css={{
                ...itemPosition(i),
                ...(collapse
                  ? {
                      position: "relative",
                      width: "40rem",
                      minWidth: "40rem",
                      transform: "unset",
                      top: "unset",
                      marginRight: "4rem",
                      ...(i % 2 === 0
                        ? {
                            marginTop: "8rem",
                          }
                        : {
                            marginBottom: "8rem",
                          }),
                    }
                  : {}),
              }}
            >
              {render(item)}
            </CarouselItem>
          ))}
        </Container>
        {indicatorType === "dots" && (
          <Flex
            css={{
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              marginLeft: "1rem",

              ...indicatorStyle,

              ...(collapse
                ? {
                    display: "none",
                  }
                : {}),
            }}
          >
            {items.map((item, i) => (
              <IndicatorItem
                onClick={(e) => setCurrent(i)}
                key={i}
                css={i === current && active}
              ></IndicatorItem>
            ))}
          </Flex>
        )}
      </Flex>

      {indicatorType === "photos" && (
        <Flex
          css={{
            width: "100%",
            marginTop: "4rem",
            flexWrap: "nowrap",
            overflowX: "auto",
          }}
        >
          {items.map((item, i) => (
            <PhotoIndicator
              onClick={(e) => setCurrent(i)}
              key={i}
              active={i === current}
              img={item.src}
            ></PhotoIndicator>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default Carousel;
